import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { addRole, getPermissions, updateRole } from 'store/actions'; // Actions to fetch permissions and role by ID
import { Checkbox } from 'antd';
import axiosInstance from 'pages/Utility/axiosInstance';
import Breadcrumb from 'components/Common/Breadcrumb2';
import { Button } from 'reactstrap';
import { showError } from 'helpers/notification_helper';

const CreateRole = ({ initialData, onSubmit }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation(); // Get the location object, which contains the query params
  const { search } = location;
  const roleId = new URLSearchParams(search).get('id'); // Extract 'id' from query params
  const [roleData, setRoleData] = useState(null);

  const permissions = useSelector((state) => state.Permission.permissions); // Assuming permissions are in Redux store
  const permissions2 = JSON.parse(localStorage?.getItem('permissions'));
  if((!permissions2?.map(item=>item?.permissionName)?.includes("Edit Roles") && !permissions2?.map(item=>item?.permissionName)?.includes("Add Roles"))){
    navigate('/pages-403');
  }


  const [permissionGroups, setPermissionGroups] = useState([]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      roleName: initialData?.roleName || '',
      permissionIds: initialData?.permissionIds ? initialData.permissionIds.split(',') : [],
    },
    validationSchema: Yup.object({
      roleName: Yup.string().required('Role Name is required'),
    }),
    onSubmit: (values) => {
      if(values?.permissionIds?.length <=0){
        showError("Please select at least one permission")
      }else{
        const permissionIdsString = values.permissionIds.join(',');
        const roleData = {
          sp: 'usp_InsertRole',
          roleName: values.roleName,
          permissionIds: permissionIdsString,
        };
  
        if (roleId) {
          dispatch(updateRole({ 
              sp: 'usp_UpdateRole',
              roleId: roleId,
              roleName: values.roleName,
              permissionIds: permissionIdsString,
           },navigate));
        } else {
          dispatch(addRole(roleData,navigate));
        }
      }
    },
  });

  const fetchPermissions = () => {
    dispatch(getPermissions());
  };

  const fetchRoleData = async () => {
    if (roleId) {
      const response = await axiosInstance.get('', { params: { sp: 'usp_GetRolesById', roleId: roleId } });
      setRoleData(response?.data?.Data?.[0]);
    }
  };

  useEffect(() => {
    fetchPermissions();
    fetchRoleData();
  }, [dispatch, roleId]);

  useEffect(() => {
    if (permissions?.Data?.length) {
      const groupedPermissions = permissions?.Data?.reduce((groups, permission) => {
        if (!groups[permission.permissionGroup]) {
          groups[permission.permissionGroup] = [];
        }
        groups[permission.permissionGroup].push(permission);
        return groups;
      }, {});
      setPermissionGroups(groupedPermissions);
    }
  }, [permissions]);

  useEffect(() => {
    if (roleData) {
      formik.setValues({
        roleName: roleData?.roleName || '',
        permissionIds: roleData?.permissionIds ? roleData.permissionIds.split(',').map(id => Number(id)) : [],
      });
    }
  }, [roleData]);

  const handlePermissionChange = (checkedValues) => {
    formik.setFieldValue('permissionIds', checkedValues);
  };

  return (
    <div className="container page-content">
      <div className="d-flex justify-content-between align-items-center ms-2">
        <Breadcrumb
          title={roleId ? 'Edit Role' : 'Create New Role'}
          breadcrumbItems={[{ title: 'Dashboard', link: '/dashboard' }, { title: 'Roles', link: '/roles' }]}
        />
        <Button className="text-white bg-primary" onClick={() => navigate('/roles')}>
          Back
        </Button>
      </div>
      <form onSubmit={formik.handleSubmit} className="bg-white p-3" style={{ borderRadius: '8px' }}>
        {/* Role Name Field */}
        <div className="form-group">
          <label htmlFor="roleName">Role Name <span className='text-danger'>*</span></label>
          <input
            type="text"
            id="roleName"
            name="roleName"
            className="form-control"
            placeholder="Enter role name"
            value={formik.values.roleName}
            onChange={formik.handleChange}
          />
          {formik.touched.roleName && formik.errors.roleName && (
            <div style={{ color: 'red' }}>{formik.errors.roleName}</div>
          )}
        </div>

        {/* Permissions Section */}
        <div className="form-group mt-3">
          <div className="mb-3">
            <h5 className="text-black">Permissions</h5>
          </div>
          <Checkbox.Group
            name="permissionIds"
            value={formik.values.permissionIds}
            onChange={handlePermissionChange}
            className="d-flex flex-column gap-2"
          >
            {Object.keys(permissionGroups).map((permissionGroup) => (
              <div key={permissionGroup} className="mb-3">
                <h6>{permissionGroup}</h6>
                <div>
                  {/* Permissions under each group */}
                  {permissionGroups[permissionGroup].map((permission) => (
                    <Checkbox key={permission.permissionId} value={permission.permissionId}>
                      <span style={{ whiteSpace: 'nowrap', color: 'gray' }}>{permission.permissionName}</span>
                    </Checkbox>
                  ))}
                </div>
              </div>
            ))}
          </Checkbox.Group>
        </div>

        {/* Submit Button */}
        <div className="form-group mt-3">
          <button type="submit" className="btn btn-primary">
            {roleId ? 'Update' : 'Create'}
          </button>
        </div>
      </form>
    </div>
  );
};

CreateRole.propTypes = {
  initialData: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
};

export default CreateRole;
